export const AOPReasons = [
  'CANCELLATION PRR',
  'DISRUPTION PRR',
  'FREE EXCHANGE',
  'GOODWILL GESTURE',
  'GOODWILL GESTURE VOUCHER B4DD',
  'PRR'
];

/**
 * Sorts the products by the created and cancelled revisions
 *
 * @param {Product} a
 * @param {number|null} [a.cancelledRevision]
 * @param {number|null} [a.createdRevision]
 * @param {Product} b
 * @param {number|null} [b.cancelledRevision]
 * @param {number|null} [b.createdRevision]
 * @returns {number}
 */
export const sortByRevisions = (
  { createdRevision: aCreatedRevision = 0, cancelledRevision: aCancelledRevision },
  { createdRevision: bCreatedRevision = 0, cancelledRevision: bCancelledRevision }
) => aCreatedRevision + (aCancelledRevision ?? aCreatedRevision) - (bCreatedRevision + (bCancelledRevision || bCreatedRevision));

/**
 * Is the passenger applicable for the alert box? To be applicable,
 * * There is at least 1 confirmed ticket and at least 1 cancelled ticket with one of the AOP S3 cancellation_reason values
 * * Only 1 exchange has been carried out yet from the LATEST AOP
 * * AOP is NOT activated on the new ticket
 *
 * @param {Object} params
 * @param {Set<Product>} params.passengerProducts - all products for the passenger
 * @param {Product} [params.activeProduct] - the currently active product for the passenger
 * @returns {boolean} true if the passenger is applicable for the alert box, false otherwise
 */
export const passengerIsApplicable = ({ activeProduct, passengerProducts }) => {
  // There is at least 1 confirmed ticket for a passenger
  if (!activeProduct) {
    return false;
  }

  const sortedPassengerProducts = [...passengerProducts].sort((a, b) => sortByRevisions(a, b));

  // 1 cancelled ticket with one of the AOP S3 cancellation_reason values
  const lastAOPCancelledTicketIndex = sortedPassengerProducts.findLastIndex(({ cancellationReason }) =>
    AOPReasons.includes(cancellationReason)
  );

  if (lastAOPCancelledTicketIndex === -1) {
    return false;
  }

  // Only 1 exchange has been carried out yet from the LATEST AOP
  const newerPassengerProducts = sortedPassengerProducts.slice(lastAOPCancelledTicketIndex + 1);
  const exchanges = newerPassengerProducts.reduce(
    ({ createdRevision, total }, { createdRevision: currentCreatedRevision }) =>
      currentCreatedRevision !== createdRevision
        ? { createdRevision: currentCreatedRevision, total: total + 1 }
        : { createdRevision, total },
    { createdRevision: 0, total: 0 }
  );

  const { total: numberOfExchanges } = exchanges;

  if (numberOfExchanges > 1) {
    return false;
  }

  // AOP is NOT activated on the new ticket
  const { afterSalesCurrentOverrides } = activeProduct;
  const isActiveAOP = afterSalesCurrentOverrides.some(({ reasonCode }) => AOPReasons.includes(reasonCode));

  if (isActiveAOP) {
    return false;
  }

  return true;
};

/**
 * Should show the AOP alert box?
 *
 * The alert box should be shown if at least one call to `passengerIsApplicable` returns true
 *
 * @param {Object} param
 * @param {Journey} param.journey - the Journey
 * @param {Array<{ products: Array<Product>}>} param.withExchangedProducts - all products in the journey, including exchanged products
 * @returns {boolean}
 */
export const shouldShowAlert = ({ journey, withExchangedProducts }) => {
  const { products } = journey;
  const activePassengerProducts = new Map(products.map(({ passengerId, ...product }) => [passengerId, product]));

  const allProducts = withExchangedProducts.flatMap(({ products }) => products);
  const passengerProductsMap = new Map(allProducts.map(({ passengerId }) => [passengerId, new Set()]));

  for (const product of allProducts) {
    const { passengerId } = product;
    const passengerProducts = passengerProductsMap.get(passengerId);
    passengerProducts.add(product);
    passengerProductsMap.set(passengerId, passengerProducts);
  }

  for (const [passengerId, passengerProducts] of passengerProductsMap) {
    const activeProduct = activePassengerProducts.get(passengerId);
    const isApplicable = passengerIsApplicable({ activeProduct, passengerProducts });
    if (isApplicable) {
      return true;
    }
  }

  return false;
};
